/**
 * sl-9qm.4.3: SearchBar Component
 *
 * Full-text search across visible columns.
 * Debounced input (300ms).
 */
import { createElement, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  placeholder: z.string().default('Search…'),
  searchColumns: z.array(z.string()).default([]),
  debounceMs: z.number().default(300),
});

function SearchBarRenderer({ config }: ViewComponentRenderProps) {
  const { placeholder, debounceMs } = config as z.infer<typeof configSchema>;
  const [inputValue, setInputValue] = useState('');
  const [_debouncedValue, setDebouncedValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue, debounceMs]);

  return createElement(
    'div',
    {
      role: 'search',
      'aria-label': 'Search',
      className: styles.searchBar,
      'data-sl-role': 'search',
    },
    createElement(
      'div',
      { className: styles.searchBox },
      createElement(
        'span',
        {
          'aria-hidden': 'true',
          className: styles.searchIcon,
        },
        '⌕'
      ),
      createElement('input', {
        type: 'search',
        placeholder,
        value: inputValue,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          setInputValue(e.target.value),
        'aria-label': placeholder,
        className: styles.searchInput,
      }),
      inputValue.length > 0 &&
        createElement(
          'button',
          {
            onClick: () => setInputValue(''),
            className: styles.searchClearButton,
            'aria-label': 'Clear search',
          },
          '✕'
        )
    )
  );
}

export const SearchBarComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.SEARCH_BAR,
  type: 'navigation',
  name: 'Search Bar',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '⌕'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    placeholder: 'Search…',
    searchColumns: [],
    debounceMs: 300,
  },
  render(props) {
    return createElement(SearchBarRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '⌕ Search Bar'
    );
  },
};
